import axios from 'axios';
import type {
  UserListItem,
  UserDetailResponse,
  CreateUserRequest,
  UpdateUserRequest,
  ChangeRoleRequest,
  PageResponse,
  UserSearchCondition,
  RoleListItem,
} from '../types/user';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const userApi = {
  /** GET /api/eraf/users — 목록 (페이징/검색/필터/정렬) */
  list: (params: UserSearchCondition): Promise<PageResponse<UserListItem>> => {
    const searchParams = new URLSearchParams();
    if (params.keyword) searchParams.set('keyword', params.keyword);
    if (params.role) searchParams.set('role', params.role);
    if (params.enabled !== undefined) searchParams.set('enabled', String(params.enabled));
    if (params.locked !== undefined) searchParams.set('locked', String(params.locked));
    if (params.page !== undefined) searchParams.set('page', String(params.page));
    if (params.size !== undefined) searchParams.set('size', String(params.size));
    if (params.sort) searchParams.set('sort', params.sort);

    return apiClient
      .get<unknown, { data: PageResponse<UserListItem> }>(`/users?${searchParams.toString()}`)
      .then((res) => res.data);
  },

  /** GET /api/eraf/users/{id} — 상세 */
  get: (id: number): Promise<UserDetailResponse> =>
    apiClient
      .get<unknown, { data: UserDetailResponse }>(`/users/${id}`)
      .then((res) => res.data),

  /** POST /api/eraf/users — 신규 생성 */
  create: (body: CreateUserRequest): Promise<UserDetailResponse> =>
    apiClient
      .post<unknown, { data: UserDetailResponse }>('/users', body)
      .then((res) => res.data),

  /** PATCH /api/eraf/users/{id} — 부분 갱신 */
  update: (id: number, body: UpdateUserRequest): Promise<UserDetailResponse> =>
    apiClient
      .patch<unknown, { data: UserDetailResponse }>(`/users/${id}`, body)
      .then((res) => res.data),

  /** DELETE /api/eraf/users/{id} — 소프트 삭제 */
  delete: (id: number): Promise<void> =>
    apiClient.delete(`/users/${id}`).then(() => undefined),

  /** PATCH /api/eraf/users/{id}/role — 역할 변경 */
  changeRole: (id: number, body: ChangeRoleRequest): Promise<UserDetailResponse> =>
    apiClient
      .patch<unknown, { data: UserDetailResponse }>(`/users/${id}/role`, body)
      .then((res) => res.data),

  /** PATCH /api/eraf/users/{id}/lock — 잠금 */
  lock: (id: number, reason?: string): Promise<UserDetailResponse> =>
    apiClient
      .patch<unknown, { data: UserDetailResponse }>(`/users/${id}/lock`, reason ? { reason } : {})
      .then((res) => res.data),

  /** PATCH /api/eraf/users/{id}/unlock — 잠금 해제 */
  unlock: (id: number): Promise<UserDetailResponse> =>
    apiClient
      .patch<unknown, { data: UserDetailResponse }>(`/users/${id}/unlock`, {})
      .then((res) => res.data),

  /** GET /api/eraf/roles?page=0&size=200 — 할당 가능한 역할 목록 */
  listRoles: (): Promise<PageResponse<RoleListItem>> =>
    apiClient
      .get<unknown, { data: PageResponse<RoleListItem> }>('/roles?page=0&size=200')
      .then((res) => res.data),

  /** GET /api/eraf/users/export — 엑셀 다운로드 (blob) */
  export: (params: Omit<UserSearchCondition, 'page' | 'size'>): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    if (params.keyword) searchParams.set('keyword', params.keyword);
    if (params.role) searchParams.set('role', params.role);
    if (params.enabled !== undefined) searchParams.set('enabled', String(params.enabled));
    if (params.locked !== undefined) searchParams.set('locked', String(params.locked));
    if (params.sort) searchParams.set('sort', params.sort);

    return apiClient
      .get(`/users/export?${searchParams.toString()}`, { responseType: 'blob' })
      .then((res) => res.data as Blob);
  },
};
